import { useCallback, useEffect, useState } from 'react';
import type { MaskedSettings } from '@ozon-ai/apigateway/contracts';
import type { SellmonitorOAuthStatus } from '@ozon-ai/sellmonitor/contracts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useTelegram } from '../../../context/TelegramContext';
import { SETTINGS_ACCESS } from '../settings-paths';
import {
  integrationStatusLabel,
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
  SettingsSuccessAlert,
} from '../settings-shared';

interface ServiceStatus {
  sellmonitor: { ok: boolean; error?: string };
}

export function SettingsSellmonitorPage() {
  const { webApp } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [oauthStatus, setOauthStatus] = useState<SellmonitorOAuthStatus | null>(null);
  const [showManualToken, setShowManualToken] = useState(false);
  const [sellmonitorToken, setSellmonitorToken] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<MaskedSettings>('/settings');
      if (data) {
        setSellmonitorToken(data.secrets.sellmonitor.accessToken);
        setShowManualToken(
          !data.secrets.sellmonitor.oauthConnected && !!data.secrets.sellmonitor.accessToken,
        );
      }
      try {
        const st = await api.get<ServiceStatus>('/settings/status');
        setStatus(st);
      } catch {
        setStatus(null);
      }
      try {
        const oauth = await api.get<SellmonitorOAuthStatus>('/sellmonitor/oauth/status');
        setOauthStatus(oauth);
      } catch {
        setOauthStatus(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function connectSellmonitor() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.get<{ authUrl: string; state: string }>('/sellmonitor/oauth/start');
      if (result?.authUrl) {
        if (webApp?.openLink) {
          webApp.openLink(result.authUrl);
        } else {
          window.open(result.authUrl, '_blank');
        }
        setMessage('Откройте браузер для авторизации Sellmonitor. После подключения обновите страницу.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось начать OAuth');
    } finally {
      setBusy(false);
    }
  }

  async function disconnectSellmonitor() {
    setBusy(true);
    setMessage(null);
    try {
      await api.post('/sellmonitor/oauth/disconnect');
      setMessage('Sellmonitor отключён');
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отключить');
    } finally {
      setBusy(false);
    }
  }

  async function saveManualToken() {
    setBusy(true);
    setMessage(null);
    try {
      await api.put('/settings/secrets', {
        sellmonitor: { accessToken: sellmonitorToken },
      });
      setMessage('Токен Sellmonitor сохранён');
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  const sellmonitorConnected = oauthStatus?.connected || status?.sellmonitor.ok;
  const statusText = sellmonitorConnected
    ? oauthStatus?.connected
      ? 'OAuth'
      : 'Подключён'
    : oauthStatus?.needsRefresh
      ? 'Токен истёк'
      : integrationStatusLabel(status?.sellmonitor.ok, status?.sellmonitor.error);

  return (
    <SettingsSubPageShell title="Sellmonitor" backTo={SETTINGS_ACCESS}>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {message && <SettingsSuccessAlert>{message}</SettingsSuccessAlert>}

      <SettingsFormCard>
        <p className="text-sm">
          Статус:{' '}
          <span className="font-semibold">{statusText}</span>
        </p>
      </SettingsFormCard>

      <SettingsFormCard>
        {oauthStatus?.connected ? (
          <div className="flex flex-col gap-3">
            <SettingsSuccessAlert>Подключён через OAuth</SettingsSuccessAlert>
            {oauthStatus.expiresAt && (
              <SettingsHint>
                Токен истекает: {new Date(oauthStatus.expiresAt).toLocaleString('ru-RU')}
              </SettingsHint>
            )}
            <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={() => void disconnectSellmonitor()}>
              Отключить Sellmonitor
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <SettingsHint>Подключите аккаунт Sellmonitor для аналитики маркетплейсов.</SettingsHint>
            <Button type="button" className="w-full" disabled={busy} onClick={() => void connectSellmonitor()}>
              Подключить Sellmonitor
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setShowManualToken(!showManualToken)}
            >
              {showManualToken ? 'Скрыть ручной ввод' : 'Ручной ввод токена (fallback)'}
            </Button>
          </div>
        )}

        {showManualToken && !oauthStatus?.connected && (
          <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sellmonitorToken">Sellmonitor access token</Label>
              <Input
                id="sellmonitorToken"
                value={sellmonitorToken}
                autoComplete="off"
                placeholder="Bearer token (если OAuth недоступен)"
                onChange={(event) => setSellmonitorToken(event.target.value)}
              />
            </div>
            <Button type="button" className="w-full" disabled={busy} onClick={() => void saveManualToken()}>
              Сохранить токен
            </Button>
          </div>
        )}
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}
